import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Loader2,
  ChevronRight,
  Users,
  ListChecks,
  CheckCircle2,
  Circle,
  X,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function AdminKnowledge() {
  const [tab, setTab] = useState("questions");

  return (
    <div>
      {/* Sub-tab pills */}
      <div className="flex items-center gap-2 mb-6">
        <SubTab
          active={tab === "questions"}
          onClick={() => setTab("questions")}
          icon={ListChecks}
          testid="adminkb-tab-questions"
        >
          Questions
        </SubTab>
        <SubTab
          active={tab === "answers"}
          onClick={() => setTab("answers")}
          icon={Users}
          testid="adminkb-tab-answers"
        >
          User Answers
        </SubTab>
      </div>

      {tab === "questions" ? <QuestionsManager /> : <UserAnswersBrowser />}
    </div>
  );
}

const SubTab = ({ active, onClick, icon: Icon, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
      active
        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
        : "bg-transparent text-zinc-400 border-zinc-800 hover:text-white hover:border-zinc-700"
    }`}
  >
    <Icon className="w-4 h-4" />
    {children}
  </button>
);

/* ===== Questions Manager ===== */
function QuestionsManager() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // existing q being edited or {} for new
  const [savingOrder, setSavingOrder] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return api
      .get("/admin/knowledge/questions", { params: { _: Date.now() } })
      .then((r) => setQuestions(r.data))
      .catch(() => toast.error("Failed to load questions"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (idx, direction) => {
    if (savingOrder) return;
    const next = [...questions];
    const target = idx + direction;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setQuestions(next);
    setSavingOrder(true);
    try {
      await api.post("/admin/knowledge/questions/reorder", {
        ordered_ids: next.map((q) => q.id),
      });
    } catch {
      toast.error("Failed to reorder");
      await load();
    } finally {
      setSavingOrder(false);
    }
  };

  const toggleActive = async (q) => {
    try {
      await api.patch(`/admin/knowledge/questions/${q.id}`, { active: !q.active });
      await load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete this question?\n\n"${q.prompt}"\n\nAll user answers for it will also be cleared.`)) return;
    try {
      await api.delete(`/admin/knowledge/questions/${q.id}`);
      toast.success("Question deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-400">
          {questions.length} question{questions.length === 1 ? "" : "s"} configured
        </div>
        <button
          data-testid="adminkb-new-question"
          onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2 transition-colors glow-primary"
        >
          <Plus className="w-4 h-4" /> New question
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm py-12 text-center">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading...
        </div>
      ) : questions.length === 0 ? (
        <EmptyQuestions onAdd={() => setEditing({})} />
      ) : (
        <div className="space-y-2">
          {questions.map((q, i) => (
            <div
              key={q.id}
              data-testid={`adminkb-row-${q.id}`}
              className={`rounded-xl border bg-zinc-950/40 p-4 flex items-start gap-3 ${
                q.active ? "border-zinc-800" : "border-zinc-800 opacity-60"
              }`}
            >
              <div className="flex flex-col items-center gap-1 pt-1">
                <button
                  data-testid={`adminkb-up-${q.id}`}
                  onClick={() => move(i, -1)}
                  disabled={i === 0 || savingOrder}
                  className="text-zinc-500 hover:text-emerald-300 disabled:opacity-30"
                  title="Move up"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] text-zinc-500 font-mono">{i + 1}</span>
                <button
                  data-testid={`adminkb-down-${q.id}`}
                  onClick={() => move(i, +1)}
                  disabled={i === questions.length - 1 || savingOrder}
                  className="text-zinc-500 hover:text-emerald-300 disabled:opacity-30"
                  title="Move down"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-medium text-white text-[15px] mb-1">{q.prompt}</div>
                {q.help_text && (
                  <div className="text-xs text-zinc-500 mb-1.5">{q.help_text}</div>
                )}
                {q.placeholder && (
                  <div className="text-[11px] text-zinc-600 italic">
                    placeholder: {q.placeholder}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  data-testid={`adminkb-toggle-${q.id}`}
                  onClick={() => toggleActive(q)}
                  className={`p-1.5 rounded-md border transition-colors ${
                    q.active
                      ? "text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10"
                      : "text-zinc-500 border-zinc-800 hover:text-white"
                  }`}
                  title={q.active ? "Active (click to hide)" : "Hidden (click to activate)"}
                >
                  {q.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button
                  data-testid={`adminkb-edit-${q.id}`}
                  onClick={() => setEditing(q)}
                  className="p-1.5 rounded-md border border-zinc-800 text-zinc-400 hover:text-white transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid={`adminkb-delete-${q.id}`}
                  onClick={() => remove(q)}
                  className="p-1.5 rounded-md border border-zinc-800 text-zinc-400 hover:text-rose-400 hover:border-rose-500/40 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <QuestionEditor
          question={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function EmptyQuestions({ onAdd }) {
  return (
    <div className="text-center py-16 border border-dashed border-zinc-800 rounded-2xl">
      <ListChecks className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
      <div className="font-display text-xl tracking-tight">No questions yet</div>
      <div className="text-sm text-zinc-500 mt-1 mb-5">
        Create the first Knowledge Base question users will answer.
      </div>
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2"
      >
        <Plus className="w-4 h-4" /> New question
      </button>
    </div>
  );
}

function QuestionEditor({ question, onClose, onSaved }) {
  const isNew = !question.id;
  const [prompt, setPrompt] = useState(question.prompt || "");
  const [helpText, setHelpText] = useState(question.help_text || "");
  const [placeholder, setPlaceholder] = useState(question.placeholder || "");
  const [active, setActive] = useState(question.active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!prompt.trim()) {
      toast.error("Question text is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/knowledge/questions", {
          prompt: prompt.trim(),
          help_text: helpText.trim() || null,
          placeholder: placeholder.trim() || null,
          active,
        });
        toast.success("Question added");
      } else {
        await api.patch(`/admin/knowledge/questions/${question.id}`, {
          prompt: prompt.trim(),
          help_text: helpText.trim(),
          placeholder: placeholder.trim(),
          active,
        });
        toast.success("Question updated");
      }
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-900">
          <div className="font-display text-lg tracking-tight font-bold">
            {isNew ? "New Question" : "Edit Question"}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <Field
            label="Question prompt"
            required
            value={prompt}
            onChange={setPrompt}
            testid="adminkb-editor-prompt"
            multiline
            placeholder="e.g., What painful problem does your business solve?"
          />
          <Field
            label="Help text (optional)"
            value={helpText}
            onChange={setHelpText}
            testid="adminkb-editor-help"
            multiline
            placeholder="Shown as subtle hint below the question."
          />
          <Field
            label="Placeholder (optional)"
            value={placeholder}
            onChange={setPlaceholder}
            testid="adminkb-editor-placeholder"
            placeholder="Shown inside the empty textarea (e.g., ₹50 lakh / $60K)"
          />
          <label className="inline-flex items-center gap-2 text-sm text-zinc-300 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="accent-emerald-500"
              data-testid="adminkb-editor-active"
            />
            <span>Active — shown to users</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-900">
          <button
            onClick={onClose}
            className="text-sm text-zinc-400 hover:text-white px-3 py-2"
          >
            Cancel
          </button>
          <button
            data-testid="adminkb-editor-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-4 py-2"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isNew ? "Add question" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, required, value, onChange, multiline, placeholder, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">
      {label}
      {required && <span className="text-rose-400 ml-1">*</span>}
    </label>
    {multiline ? (
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50 resize-y"
      />
    ) : (
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50"
      />
    )}
  </div>
);

/* ===== User Answers Browser ===== */
function UserAnswersBrowser() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api
      .get("/admin/knowledge/users", { params: { _: Date.now() } })
      .then((r) => setUsers(r.data))
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase().trim();
    return users.filter((u) =>
      `${u.name || ""} ${u.email || ""} ${u.business_name || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [users, search]);

  const open = async (u) => {
    setSelected(u.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/knowledge/users/${u.id}`, {
        params: { _: Date.now() },
      });
      setDetail(r.data);
    } catch {
      toast.error("Failed to load user knowledge");
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 h-[calc(100vh-330px)] min-h-[480px]">
      {/* Users list */}
      <aside className="flex flex-col border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden min-h-0">
        <div className="p-4 border-b border-zinc-900">
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300/80 mb-3">
            users · {filtered.length}
          </div>
          <input
            data-testid="adminkb-user-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or business..."
            className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-emerald-500/50"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="text-xs text-zinc-500 p-6 text-center">Loading...</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-xs text-zinc-500 p-6 text-center">
              No users match this search.
            </div>
          )}
          {filtered.map((u) => (
            <button
              key={u.id}
              data-testid={`adminkb-user-row-${u.id}`}
              onClick={() => open(u)}
              className={`w-full text-left px-4 py-3 border-b border-zinc-900 transition-colors ${
                selected === u.id
                  ? "bg-emerald-500/10 border-l-2 border-l-emerald-400"
                  : "hover:bg-zinc-900/60"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-sm text-white font-medium truncate">
                  {u.name || u.email}
                </div>
                <span className="text-[10px] text-zinc-500 shrink-0 font-mono">
                  {u.answered_count}/{u.total_active_questions}
                </span>
              </div>
              <div className="text-[11px] text-zinc-500 truncate">
                {u.email}
                {u.business_name && <> · {u.business_name}</>}
              </div>
              <div className="mt-2 h-1 bg-zinc-900 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300"
                  style={{
                    width: `${
                      u.total_active_questions
                        ? Math.round((u.answered_count / u.total_active_questions) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Detail */}
      <section className="flex flex-col border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden min-h-0">
        {!selected ? (
          <DetailEmpty />
        ) : detailLoading || !detail ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
          </div>
        ) : (
          <UserDetail data={detail} />
        )}
      </section>
    </div>
  );
}

const DetailEmpty = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
    <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
      <Users className="w-6 h-6 text-emerald-300" />
    </div>
    <h3 className="font-display text-2xl tracking-tighter font-black text-white">
      Pick a user
    </h3>
    <p className="text-zinc-500 text-sm mt-2 max-w-sm">
      Select a user on the left to see what they've shared in their Knowledge Base.
    </p>
  </div>
);

function UserDetail({ data }) {
  const { user, qa } = data;
  const answered = qa.filter((x) => (x.answer || "").trim()).length;
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(`/admin/knowledge/users/${user.id}/download`, {
        responseType: "blob",
      });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `knowledge-${user.id.slice(0, 8)}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Knowledge profile downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="px-5 py-4 border-b border-zinc-900 bg-zinc-950/60">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300/80 truncate">
            knowledge profile
          </div>
          <button
            data-testid="adminkb-download-user"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-300 border border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-40"
            title="Download this user's KB answers as PDF"
          >
            {downloading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Download className="w-3 h-3" />
            )}
            Download PDF
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-white font-medium">{user?.name || "—"}</span>
          {user?.email && (
            <span className="text-zinc-500 text-xs">· {user.email}</span>
          )}
          {user?.business_name && (
            <span className="text-zinc-500 text-xs">· {user.business_name}</span>
          )}
          <span className="text-zinc-600 text-xs ml-auto">
            {answered}/{qa.length} answered
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 md:px-8 py-6 space-y-5">
        {qa.map((row, i) => {
          const filled = (row.answer || "").trim().length > 0;
          return (
            <div key={row.question_id} data-testid={`adminkb-qa-${row.question_id}`}>
              <div className="flex items-start gap-3">
                {filled ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-zinc-700 mt-1 shrink-0" />
                )}
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                    Question {String(i + 1).padStart(2, "0")}
                    {!row.active && (
                      <span className="ml-2 text-amber-300/80">· inactive</span>
                    )}
                  </div>
                  <div className="text-[14px] text-white font-medium mb-2">
                    {row.prompt}
                  </div>
                  {filled ? (
                    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 text-[14px] text-zinc-200 leading-relaxed whitespace-pre-wrap">
                      {row.answer}
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-600 italic">Not answered</div>
                  )}
                  {row.updated_at && (
                    <div className="text-[10px] text-zinc-600 mt-1.5">
                      Updated {fmtDate(row.updated_at)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
